/*
 * Generador central de contenido SEO (CommonJS) compartido por:
 *  - la app React (vistas CityLanding y ServiceCityLanding)
 *  - el script de build scripts/generate-seo.js (prerender + sitemap)
 *
 * Mantener una sola fuente evita que el HTML estatico y el render de React
 * se desincronicen (titulos, descripciones, canonicals y JSON-LD).
 */
const cities = require('./cities.json');
const services = require('./services.json');

const SITE_URL = 'https://movex.com.co';

const fill = (str, city) =>
  String(str).replace(/\{city\}/g, city.name).replace(/\{region\}/g, city.region);

const getCity = (slug) => cities.find((c) => c.slug === slug);
const getService = (slug) => services.find((s) => s.slug === slug);

/* ---------- Pagina de ciudad: /grua/:city ---------- */
function cityMeta(city) {
  const url = `${SITE_URL}/grua/${city.slug}`;
  const title = `Grúa en ${city.name} | Grúa para carro, moto, maquinaria y grúa niñera - Movex`;
  const description = `¿Necesitas una grúa en ${city.name}? Solicita grúa para carro, grúa para moto, grúas para maquinaria o grúa niñera en ${city.name} (${city.region}) con Movex. Precio y tiempo estimado antes de confirmar, conductor verificado y seguimiento en tiempo real. Servicio de grúa y auxilio vial 24 horas.`;
  const keywords = `grúa en ${city.name}, grúa ${city.name}, grúa para carro ${city.name}, grúa para moto ${city.name}, grúas para maquinaria ${city.name}, grúa niñera ${city.name}, grúa cerca de mí, auxilio vial ${city.name}, servicio de grúa 24 horas, Movex`;
  const h1 = `Grúa en ${city.name}: grúa para carro, moto, maquinaria y grúa niñera`;
  return { url, title, description, keywords, h1 };
}

function cityJsonLd(city) {
  const { url, description } = cityMeta(city);
  const business = {
    '@context': 'https://schema.org',
    '@type': 'AutomotiveBusiness',
    name: `Movex - Grúas en ${city.name}`,
    image: `${SITE_URL}/apple-icon.png`,
    url,
    priceRange: '$$',
    areaServed: { '@type': 'City', name: city.name },
    description,
    makesOffer: services.map((s) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: `${s.name} en ${city.name}` },
    })),
  };
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: `Grúa en ${city.name}`, item: url },
    ],
  };
  return [business, breadcrumb];
}

/* ---------- Pagina servicio x ciudad: /grua/:service/:city ---------- */
function serviceCityMeta(service, city) {
  const url = `${SITE_URL}/grua/${service.slug}/${city.slug}`;
  const title = `${service.name} en ${city.name} | ${service.kw} 24 horas - Movex`;
  const description = `${fill(service.intro, city)} Precio y tiempo estimado antes de confirmar, conductor verificado y seguimiento en tiempo real. ${service.name} 24 horas en ${city.name}.`;
  const keywords = `${service.kw} ${city.name}, ${service.kw} en ${city.name}, ${service.kw}, grúa ${city.name}, grúa en ${city.name}, grúa cerca de mí ${city.name}, servicio de grúa 24 horas ${city.name}, Movex`;
  const h1 = `${service.name} en ${city.name}`;
  return { url, title, description, keywords, h1 };
}

function serviceCityJsonLd(service, city) {
  const { url, description } = serviceCityMeta(service, city);
  const serviceLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${service.name} en ${city.name}`,
    serviceType: service.name,
    areaServed: { '@type': 'City', name: city.name },
    provider: {
      '@type': 'AutomotiveBusiness',
      name: 'Movex',
      image: `${SITE_URL}/apple-icon.png`,
      url: `${SITE_URL}/`,
      priceRange: '$$',
    },
    url,
    description,
  };
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: `Grúa en ${city.name}`, item: `${SITE_URL}/grua/${city.slug}` },
      { '@type': 'ListItem', position: 3, name: `${service.name} en ${city.name}`, item: url },
    ],
  };
  const faq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (service.faq || []).map((f) => ({
      '@type': 'Question',
      name: fill(f.q, city),
      acceptedAnswer: { '@type': 'Answer', text: fill(f.a, city) },
    })),
  };
  return [serviceLd, breadcrumb, faq];
}

module.exports = {
  SITE_URL,
  cities,
  services,
  fill,
  getCity,
  getService,
  cityMeta,
  cityJsonLd,
  serviceCityMeta,
  serviceCityJsonLd,
};
